import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useRawMaterials } from "@/hooks/use-raw-materials";
import { useUnitConversions } from "@/hooks/use-unit-conversions";
import { getErrorMessage } from "@/lib/error-message";
import { toBaseFactor } from "@/lib/uom-conversion";
import { formatCurrency } from "@/lib/utils";
import { productRecipeService } from "@/services/product-recipe";
import { uomService } from "@/services/uom";

import type { Product } from "@artisancode/api-types";

export interface IngredientRow {
  rawMaterialId: string;
  qtyStr: string;
  uomId: string;
  note: string;
}

const emptyRow = (): IngredientRow => ({
  rawMaterialId: "",
  qtyStr: "",
  uomId: "",
  note: "",
});

/** State, real-time costing and mutations behind the "Resep & HPP" dialog. */
export function useProductRecipeForm({
  product,
  open,
  sellingPrice,
  onSuccess,
}: {
  product: Product | null;
  open: boolean;
  /** Live selling price from the product form (falls back to the saved one). */
  sellingPrice?: number;
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();

  const [yieldQtyStr, setYieldQtyStr] = useState<string>("1");
  const [items, setItems] = useState<IngredientRow[]>([emptyRow()]);
  const [recipeNote, setRecipeNote] = useState<string>("");

  const enabled = open;

  // Recipes use bahan resep and langsung pakai; barang beli is opened by repacking first.
  const { data: rawMaterialsRes } = useRawMaterials({
    per_page: 500,
    is_active: true,
    kind: "ingredient,direct",
  });
  const rawMaterials = useMemo(
    () => rawMaterialsRes?.items ?? [],
    [rawMaterialsRes],
  );

  const { data: uomsRes } = useQuery({
    queryKey: ["uoms", "list-all"],
    queryFn: () => uomService.list({ per_page: 200 }),
    enabled,
  });
  const uoms = useMemo(() => uomsRes?.items ?? [], [uomsRes]);

  const { data: conversionsRes } = useUnitConversions({ per_page: 500 });
  const conversions = useMemo(
    () => conversionsRes?.items ?? [],
    [conversionsRes],
  );

  // Recipe versions (history & active)
  const { data: recipesRes, refetch: refetchRecipes } = useQuery({
    queryKey: ["product-recipes", product?.id],
    queryFn: () =>
      product
        ? productRecipeService.getByProduct(product.id)
        : Promise.resolve({ items: [], activeRecipe: null }),
    enabled: open && !!product,
  });

  const activeRecipe = recipesRes?.activeRecipe;
  const recipeHistory = recipesRes?.items || [];

  // Populate form with active recipe when dialog opens or activeRecipe loads
  useEffect(() => {
    if (activeRecipe) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setYieldQtyStr(activeRecipe.yieldQty.toString());
      setRecipeNote(activeRecipe.note || "");
      if (activeRecipe.items && activeRecipe.items.length > 0) {
        setItems(
          activeRecipe.items.map((i) => ({
            rawMaterialId: i.rawMaterialId,
            qtyStr: i.qty.toString(),
            uomId: i.uomId,
            note: i.note || "",
          })),
        );
      } else {
        setItems([emptyRow()]);
      }
    } else if (open) {
      setYieldQtyStr("1");
      setRecipeNote("");
      setItems([emptyRow()]);
    }
  }, [activeRecipe, open]);

  // Real-time calculations
  const rawMaterialMap = useMemo(
    () => new Map(rawMaterials.map((r) => [r.id, r])),
    [rawMaterials],
  );
  const uomMap = useMemo(() => new Map(uoms.map((u) => [u.id, u])), [uoms]);

  const yieldQty = parseFloat(yieldQtyStr) || 1;

  const calculatedItems = useMemo(() => {
    return items.map((row) => {
      const rm = rawMaterialMap.get(row.rawMaterialId);
      const unitCost = rm?.costPerUnit ?? 0;
      const qty = parseFloat(row.qtyStr) || 0;
      // unitCost is per the raw material's own unit; recipe qty may be in another one (g vs kg)
      const factor =
        rm && row.uomId && row.uomId !== rm.uomId
          ? (toBaseFactor(conversions, row.uomId, rm.uomId) ?? 1)
          : 1;
      const subtotal = Math.round(qty * factor * unitCost);
      const uom =
        uomMap.get(row.uomId) || (rm ? uomMap.get(rm.uomId) : undefined);

      return {
        ...row,
        rawMaterial: rm,
        unitCost,
        qty,
        subtotal,
        uomName: uom?.symbol || uom?.name || "unit",
      };
    });
  }, [items, rawMaterialMap, uomMap, conversions]);

  const totalRecipeCost = useMemo(
    () => calculatedItems.reduce((sum, item) => sum + item.subtotal, 0),
    [calculatedItems],
  );

  const computedCogs = Math.round(
    totalRecipeCost / (yieldQty > 0 ? yieldQty : 1),
  );

  const price = sellingPrice ?? product?.sellingPrice ?? 0;
  const marginPercent =
    price > 0 ? (((price - computedCogs) / price) * 100).toFixed(1) : "0";

  // Item row handlers
  const addItem = () => setItems((prev) => [...prev, emptyRow()]);

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const changeItem = (
    index: number,
    field: keyof IngredientRow,
    value: string,
  ) => {
    setItems((prev) =>
      prev.map((row, idx) => {
        if (idx !== index) return row;
        const updated = { ...row, [field]: value };
        // Picking a raw material defaults the unit to its own base unit
        if (field === "rawMaterialId") {
          const rm = rawMaterialMap.get(value);
          if (rm) updated.uomId = rm.uomId;
        }
        return updated;
      }),
    );
  };

  const afterSave = () => {
    queryClient.invalidateQueries({
      queryKey: ["product-recipes", product?.id],
    });
    queryClient.invalidateQueries({ queryKey: ["products"] });
    refetchRecipes();
    onSuccess?.();
  };

  const createMutation = useMutation({
    mutationFn: ({
      productId,
      payload,
    }: {
      productId: string;
      payload: Parameters<typeof productRecipeService.create>[1];
    }) => productRecipeService.create(productId, payload),
  });

  const activateMutation = useMutation({
    mutationFn: (recipeId: string) =>
      product
        ? productRecipeService.activate(product.id, recipeId)
        : Promise.reject(),
    onSuccess: (activated) => {
      toast.success(
        `Resep versi ${activated.version} berhasil diaktifkan kembali.`,
      );
      afterSave();
    },
    onError: (err: Error) => {
      toast.error(getErrorMessage(err, "Gagal mengaktifkan versi resep."));
    },
  });

  const validItems = items.filter(
    (i) => i.rawMaterialId && parseFloat(i.qtyStr) > 0,
  );

  const payload = {
    yieldQty,
    yieldUomId: product?.uomId || undefined,
    items: validItems.map((i) => ({
      rawMaterialId: i.rawMaterialId,
      qty: parseFloat(i.qtyStr) || 0,
      uomId: i.uomId || rawMaterialMap.get(i.rawMaterialId)?.uomId || "",
      note: i.note.trim() || undefined,
    })),
    note: recipeNote.trim() || undefined,
  };

  const hasIngredients = validItems.length > 0;

  // Only a changed recipe becomes a new version
  const isDirty =
    !activeRecipe ||
    activeRecipe.yieldQty !== yieldQty ||
    (activeRecipe.note || "") !== (recipeNote.trim() || "") ||
    JSON.stringify(
      activeRecipe.items.map((i) => [i.rawMaterialId, Number(i.qty), i.uomId]),
    ) !==
      JSON.stringify(
        payload.items.map((i) => [i.rawMaterialId, i.qty, i.uomId]),
      );

  /** Saves the recipe for `productId` when it changed. Returns false on failure. */
  const saveRecipe = async (productId: string): Promise<boolean> => {
    if (!isDirty) return true;
    try {
      const recipe = await createMutation.mutateAsync({ productId, payload });
      toast.success(
        `Resep versi ${recipe.version} disimpan. HPP produk menjadi ${formatCurrency(recipe.computedCogs)}.`,
      );
      afterSave();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err, "Gagal menyimpan resep produk."));
      return false;
    }
  };

  return {
    rawMaterials,
    uoms,
    conversions,
    activeRecipe,
    recipeHistory,
    yieldQtyStr,
    setYieldQtyStr,
    yieldQty,
    recipeNote,
    setRecipeNote,
    calculatedItems,
    totalRecipeCost,
    computedCogs,
    storedCogs: product?.cogs ?? undefined,
    marginPercent,
    addItem,
    removeItem,
    changeItem,
    activateMutation,
    hasIngredients,
    saveRecipe,
  };
}
